'use strict';

import 'Util/drag';
import { getCurrentBreakpoint } from 'Util/util';
import CONST from 'Util/constants';

const template = document.createElement('template');
template.innerHTML = `
    <style>
    /* Style navigation buttons to sit in the middle, either side of the carousel. */
    .carousel__button--prev,
    .carousel__button--next {
        background-color: black;
        border: 1px solid black;
        border-radius: 50%;
        cursor: pointer;
        height: 3rem;
        position: absolute;
        top: 50%;
        touch-action: manipulation;
        transform: translateY(-50%);
        width: 3rem;
    }

    .carousel__button--prev {
        left: 12px;
    }

    .carousel__button--next {
        right: 12px;
    }

    /* Use pseudo elements to insert arrows inside of navigation buttons */
    .carousel__button--prev::after,
    .carousel__button--next::after {
        border-bottom: 2px solid white;
        border-right: 2px solid white;
        content: " ";
        height: 10px;
        left: 54%;
        position: absolute;
        top: 50%;
        transform: translate(-50%, -50%) rotate(135deg);
        width: 10px;
    }

    .carousel__button--prev.d-none,
    .carousel__button--next.d-none {
        display: none;
    }

    .carousel__button--next::after {
        left: 47%;
        transform: translate(-50%, -50%) rotate(-45deg);
    }

    @media (max-width: 1300px) {
        :host(.legacy-bscan) .carousel__button--prev {
            left: 20px;
        }

        :host(.legacy-bscan) .carousel__button--next {
            right: 20px;
        }
    }

    /* Adjust styling on mobile so the arrows don't overlap on the content */
    @media (max-width: ${CONST.breakpoints.lg}px) {
        .carousel__button--prev {
            left: 4px;
        }

        .carousel__button--next {
            right: 5px;
        }

        .carousel__button--prev,
        .carousel__button--next {
            background: none;
            border: none;
        }

        .carousel__button--prev::after,
        .carousel__button--next::after {
            border-color: black;
            height: 18px;
            width: 18px;
        }
    }
    </style>
    <slot></slot>
    <button class="carousel__button--prev"></button>
    <button class="carousel__button--next"></button>
`;

/**
 * @example <vz-carousel infinite="true" active-items="3">
 *
 * @export
 * @class VZCarousel
 * @extends {HTMLElement}
 */
export default class VZCarousel extends HTMLElement {
    constructor() {
        super();
        this.attachShadow({
            mode: 'open'
        });
        this.shadowRoot.appendChild(template.content.cloneNode(true));
    }

    connectedCallback() {
        this.initialized = null;
        this.infinite = this.getAttribute('infinite') === 'true';
        this.specificBreakpoints = this.getAttribute('breakpoints') && this.getAttribute('breakpoints').split(',');
        this.slideClassName = 'carousel__item';

        this.container = this.shadowRoot.querySelector('slot').assignedElements()[0];
        this.nextButton = this.shadowRoot.querySelector('.carousel__button--next');
        this.prevButton = this.shadowRoot.querySelector('.carousel__button--prev');
        this.nextButton.addEventListener('click', this.moveNext.bind(this));
        this.prevButton.addEventListener('click', this.movePrev.bind(this));

        this.setupSlideInformation.call(this);

        // If specific breakpoints have been defined in the `breakpoints` attribute, fire up determineBreakpointStyling
        if (this.specificBreakpoints) {
            window.addEventListener('load', this.determineBreakpointStyling.bind(this));
            document.addEventListener('breakpointChange', this.determineBreakpointStyling.bind(this));
        } else {
            this.initCarousel();
            document.addEventListener('breakpointChange', this.goToFirstSlide.bind(this));
        }

        // If we're inside PD, do some more initializing
        if (window.frameElement && window.frameElement.closest('.page-designer-ui')) {
            this.initPageDesigner.call(this);
        }
    }

    // If specific breakpoints have been defined, determine whether a carousel should be created or destroyed
    determineBreakpointStyling() {
        const currentBreakpoint = getCurrentBreakpoint();
        const supportCurrentBreakpoint = currentBreakpoint && this.specificBreakpoints.some((breakpoint) => breakpoint === currentBreakpoint.name);

        if (supportCurrentBreakpoint) {
            this.initCarousel();
        } else {
            this.destroyCarousel();
        }
    }

    setupSlideInformation() {
        const slotContent = this.shadowRoot.querySelector('slot').assignedElements();
        this.slides = slotContent.length > 0 ? [...slotContent[0].children] : [];
        this.slide = 0;
        this.totalSlides = this.slides.length;
    }

    getSlideWidth() {
        return document.documentElement.clientWidth > 1023 ? 100 / this.activeSlides : 100;
    }

    setSlidesWidth() {
        this.slideWidth = this.getSlideWidth();

        this.slides.forEach((item) => {
            item.style.width = `${this.slideWidth}%`;
        });
    }

    goToFirstSlide() {
        this.container.style.removeProperty('transform');
        this.setupSlideInformation.call(this);
        this.initSlides.call(this);

        if (!this.infinite) this.prevButton.classList.add('d-none');
        this.nextButton.classList.remove('d-none');
    }

    initSlides() {
        this.setSlidesWidth.call(this);
        this.activeSlides = document.documentElement.clientWidth > 1023 ? parseInt(this.getAttribute('active-slides'), 10) : 1;

        this.slides.forEach((item) => {
            item.classList.add(this.slideClassName);
        });
    }

    toggleButtonsVisibility() {
        if (!this.initialized) {
            this.nextButton.classList.add('d-none');
            this.prevButton.classList.add('d-none');
            return;
        }

        if (this.totalSlides > this.activeSlides) {
            this.nextButton.classList.remove('d-none');

            if (!this.infinite) {
                this.prevButton.classList.add('d-none');
            } else {
                this.prevButton.classList.remove('d-none');
            }
        } else {
            this.nextButton.classList.add('d-none');
            this.prevButton.classList.add('d-none');
        }
    }

    // Create the carousel
    initCarousel() {
        if (this.initialized || !(this.totalSlides > 0)) return;

        this.initialized = true;
        this.moving = true;
        this.classList.add('carousel-wrapper');
        this.container.classList.add('carousel', 'flex-nowrap');
        this.container.classList.remove('pe-none');
        this.initSlides.call(this);
        this.supportDrag.bind(this)(this.container);
        this.toggleButtonsVisibility.call(this);

        this.moving = false;
        this.classList.add('initialized');
    }

    // Destroy the carousel
    destroyCarousel() {
        if (typeof this.initialized === 'boolean' && !this.initialized) return;

        this.classList.remove('carousel-wrapper');
        this.container.classList.remove('carousel', 'flex-nowrap');
        this.container.classList.add('pe-none'); // Will disable dragging
        this.container.style.removeProperty('transform'); // Will remove any dragged value
        this.slide = 0; // Reset to first slide
        this.moving = false;
        this.initialized = false;
        this.classList.remove('initialized');
        this.toggleButtonsVisibility.call(this);

        this.slides.forEach((item) => {
            item.style.removeProperty('width');
        });
    }

    move() {
        if (this.moving || this.totalSlides === 1) return;

        const nextBatch = this.slide + this.activeSlides;
        let amountOfSlidesToMove = 100 * (this.slide / this.activeSlides); // As a percentage in steps of 100%

        if (nextBatch >= this.totalSlides) {
            const amountOfLastSlides = this.totalSlides - this.slide;

            amountOfSlidesToMove -= 100; // First "go back" to the previous amount
            amountOfSlidesToMove += this.slideWidth * amountOfLastSlides; // Then add the actual amount of last slides to show
        }

        this.container.style.transform = `translate3d(-${amountOfSlidesToMove}%, 0px, 0px)`;

        if (!this.infinite) {
            if (this.slide === 0) {
                this.prevButton.classList.add('d-none');
            } else {
                this.prevButton.classList.remove('d-none');
            }

            if (nextBatch >= this.totalSlides) {
                this.nextButton.classList.add('d-none');
            } else {
                this.nextButton.classList.remove('d-none');
            }
        }
    }

    moveNext() {
        if (this.moving) return;
        if (this.slide + this.activeSlides >= this.totalSlides && !this.infinite) return;
        if (this.slide + this.activeSlides >= this.totalSlides) {
            this.slide = 0;
        } else {
            this.slide += this.activeSlides;
        }
        this.move();
    }

    movePrev() {
        if (this.moving) return;
        if (this.slide <= 0 && !this.infinite) return;
        if (this.slide <= 0) {
            this.slide = this.totalSlides - 1;
        } else {
            this.slide -= this.activeSlides;
        }
        this.move();
    }

    supportDrag(item) {
        // Prevent native image dragging.
        item.ondragstart = () => {
            return false;
        };

        item.sdrag(
            (el) => {
                this.container.classList.add('dragging');
                el.style.cursor = 'grabbing';
            },
            (el) => {
                this.container.classList.remove('dragging');

                // Allow button clicks, etc.
                if (['A', 'BUTTON', 'LABEL', 'INPUT', 'SELECT'].includes(el.nodeName)) {
                    el.style.left = '';
                    return;
                }

                if (el.style.left.length === 0 || Math.abs(parseInt(el.style.left, 10)) < 20) return; // Accidental drag
                if (parseInt(el.style.left, 10) > 0) {
                    this.movePrev();
                } else {
                    this.moveNext();
                }
                // reset the left to ensure correct position when navigating back.
                el.style.left = '';
            },
            'horizontal'
        );
    }

    // Check if window is "loaded" with an interval, seeing as the initial execution (nor window.load) does not do much when inside PD
    initPageDesigner() {
        const interval = setInterval(checkIfLoaded.bind(this), 1000); // eslint-disable-line no-use-before-define

        /**
         * @private checkIfLoaded
         *
         * @description
         * Will check to see if the PD window is finally done loading, then call initCarousel
         */
        function checkIfLoaded() {
            if (document.documentElement.clientWidth > 0) {
                this.determineBreakpointStyling.call(this);
                clearInterval(interval);
            }
        }
    }
}

if (!window.customElements.get('vz-carousel')) {
    window.customElements.define('vz-carousel', VZCarousel);
}
